import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  authListUsers, authCreateUser, authUpdateUser, authDeleteUser,
  authResetPassword, authPermissions,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG = "#F8F9FA";

const PERM_LABEL = {
  view_admin_home: "Admin Home (MRR stats, integrations)",
  view_subscribers: "Paid Subscribers list",
  view_rep_leaderboard: "Rep Leaderboard (whole team)",
  view_rep_self: "My Own Rep Stats only (requires rep slug)",
  view_ncge: "NCGE Ad Campaign queue",
  view_referrals: "Referral Payouts",
  view_retention: "Retention Saves",
  view_marketing: "Marketing / Review Wins",
  view_inbox: "Contact & Suggestion Inbox",
};

const apiErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong.";
};

export default function AdminUsersPage() {
  const { user: me, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ users }, { permissions }] = await Promise.all([authListUsers(), authPermissions()]);
      setUsers(users);
      setAllPerms(permissions);
    } catch (e) {
      setError(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete ${u.email}? They will lose access immediately.`)) return;
    try {
      await authDeleteUser(u.id);
      await load();
    } catch (e) {
      alert(apiErr(e));
    }
  };

  const handleResetPw = async (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 8 chars):`);
    if (!pw) return;
    try {
      await authResetPassword(u.id, pw);
      alert(`Password for ${u.email} updated. Share it with them privately.`);
    } catch (e) {
      alert(apiErr(e));
    }
  };

  return (
    <div style={{ background: BG, minHeight: "100vh", padding: "32px 24px" }} data-testid="admin-users-page">
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>OWNER · USER MANAGEMENT</div>
            <h1 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, fontSize: 44, color: NAVY, margin: "4px 0 0" }}>
              WHO CAN SEE WHAT
            </h1>
            <p style={{ color: "#5b5e69", marginTop: 4 }}>
              Signed in as <strong>{me?.email}</strong> · <span style={{ color: GOLD }}>OWNER</span>
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setShowCreate(true)} style={btnPrimary} data-testid="users-add-btn">
              + ADD USER
            </button>
            <Link to="/admin/change-password" style={{ ...btnGhost, textDecoration: "none", display: "inline-flex", alignItems: "center" }} data-testid="users-change-pw-link">
              CHANGE MY PASSWORD
            </Link>
            <button onClick={logout} style={btnGhost} data-testid="users-logout-btn">
              SIGN OUT
            </button>
          </div>
        </div>

        {error && <div style={errBox}>{error}</div>}

        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e5e7ee", marginTop: 24, overflow: "auto" }}>
          {loading ? (
            <div style={{ padding: 32, color: "#5b5e69" }}>Loading…</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }} data-testid="users-table">
              <thead style={{ background: BG }}>
                <tr>
                  <th style={th}>User</th>
                  <th style={th}>Role</th>
                  <th style={th}>Permissions</th>
                  <th style={th}>Rep Slug</th>
                  <th style={th}>Status</th>
                  <th style={th}>Last Login</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} style={{ borderTop: "1px solid #e5e7ee" }}>
                    <td style={td}>
                      <div style={{ fontWeight: 700, color: NAVY }}>{u.name || u.email}</div>
                      <div style={{ fontSize: 12, color: "#5b5e69" }}>{u.email}</div>
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>{u.phone_masked}</div>
                    </td>
                    <td style={td}>
                      <span style={{ background: u.role === "owner" ? NAVY : "#e5e7ee", color: u.role === "owner" ? GOLD : NAVY, padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: 1.5 }}>
                        {u.role.toUpperCase()}
                      </span>
                    </td>
                    <td style={td}>
                      {u.role === "owner" ? <em style={{ color: "#5b5e69" }}>full access</em> : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {(u.permissions || []).length === 0 && <em style={{ color: "#9ca3af" }}>none</em>}
                          {(u.permissions || []).map((p) => (
                            <code key={p} style={permPill}>{p}</code>
                          ))}
                        </div>
                      )}
                    </td>
                    <td style={td}>{u.rep_slug ? <code style={permPill}>{u.rep_slug}</code> : "—"}</td>
                    <td style={td}>
                      {u.active ? <span style={{ color: "#1A7A4A", fontWeight: 700 }}>Active</span> : <span style={{ color: "#A32D2D", fontWeight: 700 }}>Disabled</span>}
                    </td>
                    <td style={td}>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "never"}</td>
                    <td style={td}>
                      {u.role !== "owner" && (
                        <>
                          <button onClick={() => setEditingId(u.id)} style={btnLink} data-testid={`users-edit-${u.id}`}>EDIT</button>
                          <button onClick={() => handleResetPw(u)} style={btnLink}>RESET PW</button>
                          <button onClick={() => handleDelete(u)} style={{ ...btnLink, color: "#A32D2D" }} data-testid={`users-delete-${u.id}`}>DELETE</button>
                        </>
                      )}
                      {u.role === "owner" && <em style={{ color: "#5b5e69", fontSize: 12 }}>rotate via .env</em>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreate && (
        <UserEditorModal
          mode="create"
          allPerms={allPerms}
          onClose={() => setShowCreate(false)}
          onSave={async (payload) => {
            try {
              await authCreateUser(payload);
              setShowCreate(false);
              await load();
            } catch (e) { alert(apiErr(e)); }
          }}
        />
      )}
      {editingId && (
        <UserEditorModal
          mode="edit"
          allPerms={allPerms}
          existing={users.find((u) => u.id === editingId)}
          onClose={() => setEditingId(null)}
          onSave={async (payload) => {
            try {
              await authUpdateUser(editingId, payload);
              setEditingId(null);
              await load();
            } catch (e) { alert(apiErr(e)); }
          }}
        />
      )}
    </div>
  );
}

function UserEditorModal({ mode, allPerms, existing, onClose, onSave }) {
  const [email, setEmail] = useState(existing?.email || "");
  const [name, setName] = useState(existing?.name || "");
  const [phone, setPhone] = useState(existing?.phone || "");
  const [password, setPassword] = useState("");
  const [permissions, setPermissions] = useState(existing?.permissions || []);
  const [repSlug, setRepSlug] = useState(existing?.rep_slug || "");
  const [active, setActive] = useState(existing?.active !== false);

  const toggle = (p) => setPermissions((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (mode === "create") {
      onSave({
        email, name, phone, password,
        role: "viewer",
        permissions,
        rep_slug: repSlug || null,
      });
    } else {
      onSave({
        name, phone,
        permissions,
        rep_slug: repSlug || null,
        active,
      });
    }
  };

  return (
    <div style={modalScrim} data-testid="user-editor-modal" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} style={modalCard}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>
          {mode === "create" ? "ADD A NEW USER" : `EDIT · ${existing?.email}`}
        </div>
        <h2 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, fontSize: 30, color: NAVY, margin: "4px 0 18px" }}>
          {mode === "create" ? "GRANT ACCESS" : "UPDATE PERMISSIONS"}
        </h2>

        {mode === "create" && (
          <>
            <Field label="EMAIL">
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} data-testid="user-email-input" />
            </Field>
            <Field label="PASSWORD (min 8 chars · share privately)">
              <input required type="text" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle} data-testid="user-password-input" />
            </Field>
          </>
        )}
        <Field label="FULL NAME">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} data-testid="user-name-input" />
        </Field>
        <Field label="PHONE (for 2FA SMS · +1XXXXXXXXXX)">
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} style={inputStyle} placeholder="+14015551234" data-testid="user-phone-input" />
        </Field>

        <Field label="PERMISSIONS">
          <div style={{ display: "grid", gap: 8 }}>
            {allPerms.map((p) => (
              <label key={p} style={permRow}>
                <input
                  type="checkbox"
                  checked={permissions.includes(p)}
                  onChange={() => toggle(p)}
                  data-testid={`perm-checkbox-${p}`}
                  style={{ width: 18, height: 18, accentColor: NAVY }}
                />
                <span style={{ flex: 1 }}>
                  <code style={permPill}>{p}</code>
                  <span style={{ color: "#5b5e69", marginLeft: 8, fontSize: 13 }}>{PERM_LABEL[p] || ""}</span>
                </span>
              </label>
            ))}
          </div>
        </Field>

        {permissions.includes("view_rep_self") && (
          <Field label="REP SLUG (the URL slug used in ?rep= during signup)">
            <input type="text" value={repSlug} onChange={(e) => setRepSlug(e.target.value)} placeholder="thomas" style={inputStyle} data-testid="user-rep-slug-input" />
          </Field>
        )}

        {mode === "edit" && (
          <Field label="STATUS">
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} style={{ width: 18, height: 18, accentColor: NAVY }} />
              Active (uncheck to disable login)
            </label>
          </Field>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <button type="submit" style={btnPrimary} data-testid="user-save-button">
            {mode === "create" ? "CREATE USER" : "SAVE CHANGES"}
          </button>
          <button type="button" onClick={onClose} style={btnGhost}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ color: NAVY, letterSpacing: 2, fontSize: 11, fontWeight: 700, marginBottom: 4 }}>{label}</div>
    {children}
  </div>
);

const th = { textAlign: "left", padding: "14px 14px", color: NAVY, fontSize: 12, letterSpacing: 1.5, fontWeight: 700, whiteSpace: "nowrap" };
const td = { padding: "12px 14px", color: NAVY, fontSize: 13, verticalAlign: "top" };
const inputStyle = { width: "100%", padding: "12px 14px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 15, outline: "none", boxSizing: "border-box" };
const btnPrimary = { background: GOLD, color: NAVY, border: "none", padding: "12px 22px", borderRadius: 10, fontWeight: 700, letterSpacing: 2, cursor: "pointer" };
const btnGhost = { background: "transparent", color: NAVY, border: `1px solid ${NAVY}`, padding: "12px 22px", borderRadius: 10, fontWeight: 700, letterSpacing: 2, cursor: "pointer" };
const btnLink = { background: "transparent", color: NAVY, border: "none", padding: "4px 8px", cursor: "pointer", fontWeight: 700, fontSize: 12, letterSpacing: 1 };
const permPill = { background: BG, padding: "2px 8px", borderRadius: 6, fontFamily: "monospace", fontSize: 12, color: NAVY };
const permRow = { display: "flex", alignItems: "center", gap: 8, padding: "6px 4px", borderBottom: "1px dashed #e5e7ee" };
const modalScrim = { position: "fixed", inset: 0, background: "rgba(10,22,40,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "60px 20px", zIndex: 1000, overflowY: "auto" };
const modalCard = { background: "#fff", borderRadius: 18, padding: 28, maxWidth: 600, width: "100%" };
const errBox = { background: "#FDECEC", color: "#A32D2D", padding: "12px 16px", borderRadius: 10, marginTop: 16 };
